import { useLang } from "hooks/useLang";
import React, { useEffect, useMemo, useRef, useState } from "react";
import convertArNumbersToEn from "utils/helpers/convertArNumbersToEn";
import CallingCodeSelector from "./CallingCodeSelector";
import useDebounce from "hooks/useDebounce";
import { validation } from "utils/helpers/validation";
import { twMerge } from "tailwind-merge";

interface IProps
  extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  setCallingCode: (callingCode: string) => void;
  setPhone: (phone: string) => void;
  setIsValidPhone?: (isValid: boolean) => void;
  callingCode: string;
  phone: string;
  error?: string | React.ReactNode;
  label?: string;
  inputContainerClassName?: string;
}

const PhoneInput = ({
  callingCode,
  setCallingCode,
  setPhone,
  setIsValidPhone,
  phone,
  error,
  label: customLabel,
  inputContainerClassName,
  ...props
}: IProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const { t } = useLang();
  const [isValidPhone, setIsValidPhoneLocal] = useState(true);
  const callingCodeSelectorRef = useRef<boolean>(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const debouncedPhone = useDebounce(phone, 500);
  const [isTyping, setIsTyping] = useState(false);

  const handleFocus = () => {
    setIsFocused(true);
  };

  // Validate on debounced input, so error clears shortly after user fixes value (without needing to blur)
  useEffect(() => {
    if (!hasInteracted) return;
    const isValid = validation.isPhoneValid(debouncedPhone) && debouncedPhone.length >= 1;
    setIsValidPhoneLocal(isValid);
    setIsValidPhone?.(isValid);
    // typing has paused (debounce fired)
    setIsTyping(false);
  }, [debouncedPhone, hasInteracted, setIsValidPhone]);

  const style = useMemo(() => {
    return {
      label: `text-sm capitalize transition-all md:text-base pb-1 text-gray-600 dark:text-gray-200 after:content-['*'] after:text-red-500 `,
      inputContainer: twMerge(
        `flex border rounded-md transition-all h-12 overflow-hidden inputContainer dark:bg-opacity-20 bg-opacity-40 dark:bg-primary-theme-500`,
        isFocused ? "border-[#7B91EF]" : "border-[#F0F0F080]",
        !isTyping && (error || !isValidPhone) ? "border-red-400" : "",
        inputContainerClassName
      ),
      passwordEye: `flex w-9 h-full cursor-pointer items-center justify-center hover:bg-gray-100 hover:dark:bg-primary-theme-400 transition-all`,
    };
  }, [isFocused, error, isValidPhone, inputContainerClassName, isTyping]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.trim();
    const sanitizedPhone = convertArNumbersToEn(inputValue);

    if (
      validation.isDigitsOnly(sanitizedPhone) &&
      (sanitizedPhone.length === 0 || sanitizedPhone[0] !== "0")
    ) {
      setPhone(sanitizedPhone);
      setHasInteracted(true);
      setIsTyping(true);
    }
  };

  const validatePhoneNumber = () => {
    if (!hasInteracted) return;

    const isValid =
      validation.isPhoneValid(phone) && phone.length >= 1;

    if (isValid) {
      setIsValidPhoneLocal(true);
      setIsValidPhone?.(true);
    } else {
      setIsValidPhoneLocal(false);
      setIsValidPhone?.(false);
    }
  };

  useEffect(() => {
    if (callingCode[0] === "+") {
      setCallingCode(callingCode.substring(1));
    }
  }, [callingCode]);

  useEffect(() => {
    return () => {
      if (callingCodeSelectorRef.current) {
        setCallingCode("966");
      }
    };
  }, []);

  return (
    <div className="flex w-full flex-col">
      <div className="px-2 py-1">
        <label className={style.label} htmlFor={"phone-input-id"}>
          {customLabel ? customLabel : t("phoneNumber")}{" "}
        </label>
      </div>
      <div
        dir="ltr"
        className={style.inputContainer}
        onFocus={handleFocus}
      // onBlur={handleBlur}
      >
        <CallingCodeSelector
          setCallingCode={(code) => {
            setCallingCode(code);
            setHasInteracted(false);
          }}
          callingCode={callingCode}
          disable={props.readOnly ? true : false}
        />
        <input
          id="phone-input-id"
          className="w-full bg-transparent p-2 text-sm placeholder-white-500 placeholder:capitalize placeholder:text-[14px]"
          // className={[style.input,]}
          onChange={handlePhoneChange}
          onBlur={validatePhoneNumber}
          value={phone}
          autoComplete="tel"
          maxLength={9}
          placeholder={t("phoneNumber")}
          {...props}
          inputMode="tel"
        />
      </div>
      {hasInteracted && !isTyping && (!isValidPhone || error) && (
        <span className="pt-1 text-xs text-red-500 dark:text-red-400">
          {error
            ? typeof error === "string"
              ? t(error)
              : error
            : t("invalidPhoneNumber")}
        </span>
      )}
    </div>
  );
};

export default PhoneInput;
